/* ══════════════════════════════════════════════════════════════════
   🚫 CHECKOUT GATE — blocks checkout in TWO situations:

   1. Any cart line is marked "_not_available" (price/update returned
      0/invalid for a pincode that otherwise IS serviceable — see
      sw-pincode-cart-recheck.js).

   2. 🆕FIX — A pincode recheck is CURRENTLY IN FLIGHT. Previously the
      gate only reacted to `_not_available`, which only gets set AFTER
      the async recheck (serviceability/price calls) finishes. That
      left a window — right after the shopper enters a new pincode,
      before prices have actually updated — where checkout was still
      clickable with STALE prices. Now sw-pincode-cart-recheck.js
      dispatches 'sleep-setup:pincode-recheck-start' the INSTANT a
      pincode change is detected (before any network call), and this
      gate locks checkout immediately on that signal. It only unlocks
      on 'sleep-setup:pincode-recheck-end' (unless that event also
      reports items are still not-available, in which case case #1
      above keeps it locked).

   These "_not_available" items are NEVER auto-removed from the cart
   (the shopper might want to keep them and wait for a pincode where
   they ARE available, or remove them manually) — but checkout must
   not proceed while any of them are present, since they have no valid
   price to charge.

   This script:
     1. Checks /cart.js for any real item with properties._not_available
        === 'true'.
     2. Listens for pincode-recheck-start/end to lock/unlock instantly,
        independent of the cart-mutation events below.
     3. If gated: shows a banner in the cart drawer summary and
        intercepts clicks on the Checkout button(s) — including the
        GoKwik-powered checkout flow — to stop navigation and show a
        message instead.
     4. Re-checks on every cart mutation (same shared event this
        theme's other scripts already listen for) so the gate lifts
        automatically the moment the last unavailable item is fixed or
        removed.
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.__swCheckoutBannerGateInit) return;
  window.__swCheckoutBannerGateInit = true;

  console.log('🚫 Checkout gate — active');

  // 🆕 Banner CSS ko JS se inject karo — ye .js asset file hai, isliye
  // <style> tag yahan seedha kaam nahi karega. Bina isske banner DOM mein
  // insert to ho jaata tha lekin koi styling na hone ki wajah se invisible
  // (0 height/no color) reh jaata tha.
  var swGateStyle = document.createElement('style');
  swGateStyle.textContent =
    '.sw-checkout-gate-banner {' +
    '  display: block; background: #fef2f2; color: #cc0000;' +
    '  border: 1px solid #f5c2c2; border-radius: 6px; padding: 10px 14px;' +
    '  font-size: 13px; font-weight: 600; line-height: 1.4; text-align: center;' +
    '  margin: 0 0 12px 0;' +
    '}' +
    '.sw-checkout-disabled {' +
    '  opacity: 0.5 !important; pointer-events: none !important; cursor: not-allowed !important;' +
    '}';
  document.head.appendChild(swGateStyle);

  var BANNER_CLASS = 'sw-checkout-gate-banner';
  var SUMMARY_SELECTORS = ['.cart-drawer__summary', '.cart-drawer__summary-padd', '.cart__ctas'];

  // 🆕 Multiple fallback selectors try karo — agar theme ka summary block
  // .cart-drawer__summary use nahi karta (jaisa cart-checkout-gate.liquid
  // mein .cart-drawer__summary-padd bhi ek host ke roop mein use hoti hai),
  // to banner kabhi insert hi nahi hota tha.
  function findSummaryEl() {
    for (var i = 0; i < SUMMARY_SELECTORS.length; i++) {
      var el = document.querySelector(SUMMARY_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // Broad selector list — covers the theme's native checkout button/link
  // AND GoKwik's own checkout trigger (onCheckoutNow / .gokwik-checkout,
  // confirmed present in layout/theme.liquid). If checkout is wired up
  // differently in some other part of the theme, add its selector here
  // too rather than relying on just one.
  var CHECKOUT_SELECTORS = [
    'a[href="/checkout"]',
    'a[href^="/checkout"]',
    'button[name="checkout"]',
    '.cart-drawer__checkout',
    '.gokwik-checkout',
    '[data-checkout-button]'
  ];

  var hasUnavailable = false;
  // 🆕FIX: separate flag for "recheck currently running" — independent
  // of hasUnavailable, since the recheck itself hasn't reported any
  // result yet while it's in flight.
  var recheckInProgress = false;

  var GATE_MESSAGE_UNAVAILABLE = 'Some items in your cart are not available at this pincode. Remove them or try a different pincode to continue.';
  var GATE_MESSAGE_UPDATING    = 'Updating prices for your pincode… please wait a moment before checking out.';

  function getCartCached() {
    if (window.__swGetCart) return window.__swGetCart();
    return fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
  }

  function checkCartForUnavailable() {
    return getCartCached().then(function (cart) {
      var items = cart.items || [];
      hasUnavailable = items.some(function (it) {
        return it.properties && it.properties['_not_available'] === 'true';
      });
      applyGateState();
      return hasUnavailable;
    }).catch(function () {
      return false;
    });
  }

  function applyGateState() {
    // 🆕FIX: gate is active if EITHER an item is not-available OR a
    // recheck is currently running.
    var gated = hasUnavailable || recheckInProgress;
    var message = recheckInProgress ? GATE_MESSAGE_UPDATING : GATE_MESSAGE_UNAVAILABLE;

    var summaryEl = findSummaryEl();
    var existingBanner = document.querySelector('.' + BANNER_CLASS);

    if (gated) {
      if (summaryEl) {
        if (!existingBanner) {
          existingBanner = document.createElement('div');
          existingBanner.className = BANNER_CLASS;
          summaryEl.insertBefore(existingBanner, summaryEl.firstChild);
        }
        // Keep banner text in sync — e.g. switches from "updating"
        // to "not available" automatically if the recheck finishes
        // and it turns out an item really is unavailable.
        existingBanner.textContent = message;
      }
      CHECKOUT_SELECTORS.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (btn) {
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('sw-checkout-disabled');
          btn.style.opacity = '0.5';
          btn.style.pointerEvents = 'none';
          btn.style.cursor = 'not-allowed';
        });
      });
    } else {
      if (existingBanner) existingBanner.remove();
      CHECKOUT_SELECTORS.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (btn) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('sw-checkout-disabled');
          btn.style.opacity = '';
          btn.style.pointerEvents = '';
          btn.style.cursor = '';
        });
      });
    }
  }

  // Belt-and-braces click intercept — in case a checkout button was
  // added to the DOM after applyGateState() last ran (e.g. right
  // after a fresh cart-drawer morph, before the next mutation event
  // fires), this catches the click itself rather than relying purely
  // on the disabled-styling pass above.
  document.addEventListener('click', function (e) {
    if (!hasUnavailable && !recheckInProgress) return;
    var isCheckoutTrigger = CHECKOUT_SELECTORS.some(function (sel) {
      return e.target.closest(sel);
    });
    if (isCheckoutTrigger) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      alert(recheckInProgress ? GATE_MESSAGE_UPDATING : GATE_MESSAGE_UNAVAILABLE);
    }
  }, true); // capture phase — must run before GoKwik's own onCheckoutNow handler

  var recheckTimer = null;
  function scheduleRecheck() {
    clearTimeout(recheckTimer);
    recheckTimer = setTimeout(checkCartForUnavailable, 300);
  }

  ['sleep-setup:cart-changed', 'cart:refresh', 'cart:updated', 'cart:change'].forEach(function (evtName) {
    document.addEventListener(evtName, scheduleRecheck);
  });

  // 🆕FIX: instant lock/unlock tied to the pincode recheck lifecycle,
  // dispatched by sw-pincode-cart-recheck.js. This is what actually
  // closes the race-condition gap — no debounce/delay here, locks the
  // very instant a pincode change is detected.
  document.addEventListener('sleep-setup:pincode-recheck-start', function () {
    recheckInProgress = true;
    applyGateState();
  });

  document.addEventListener('sleep-setup:pincode-recheck-end', function (e) {
    recheckInProgress = false;
    // Trust the signal from the recheck itself immediately...
    if (e.detail && typeof e.detail.hasUnavailable === 'boolean') {
      hasUnavailable = e.detail.hasUnavailable;
      applyGateState();
    }
    // ...then also re-verify against the actual cart shortly after
    // (covers merges/removals that happened as part of the recheck
    // but aren't reflected in e.detail).
    scheduleRecheck();
  });

  // 🆕 FIX: Shopify ka NATIVE cart-items-component X-SW-Silent-Cart-Sync
  // header ko ignore karke background price-sync (sleep-setup-widget.js
  // ke syncAllCartItemPrices) ke har /cart/change.js call par apna khud
  // ka DOM re-render/morph chala deta hai — jisse humara inserted banner
  // aur checkout-disabled classes wipe ho jaate hain. Hume ye morph kab
  // hoga pata nahi chal sakta (kisi bhi cart-mutation event se), isliye
  // seedha drawer ke DOM ko observe karo aur har baar uska content
  // replace hote hi turant gate state reapply karo — cause chahe kuch
  // bhi ho.
  var drawerObserveTimer = null;
  function observeDrawerForMorph() {
    var drawer = document.querySelector('cart-drawer-component, .cart-drawer, #cart-drawer, cart-drawer');
    if (!drawer) {
      setTimeout(observeDrawerForMorph, 500);
      return;
    }
    var observer = new MutationObserver(function () {
      clearTimeout(drawerObserveTimer);
      drawerObserveTimer = setTimeout(applyGateState, 50);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }
  observeDrawerForMorph();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkCartForUnavailable);
  } else {
    checkCartForUnavailable();
  }
})();
